import { JSX, useEffect } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';

import { Label } from 'components/Label';
import { Select } from 'components/Select';
import { Flex, Grid } from 'components/layout';
import { Typography } from 'components/typography';
import { NormalizedLoad } from 'types/Load';

import { currencyList } from '../TurvoSectionForms/constants';
import { LoadBuildingTextInput } from '../components/LoadBuildingTextInput';
import { normalizeTaiFuelCarrierLineItems } from './utils';

export function RatesForm({
  formMethods,
  showBuySellFields = false,
  isUpdateMode = false,
}: {
  formMethods: UseFormReturn<NormalizedLoad>;
  showBuySellFields?: boolean;
  isUpdateMode?: boolean;
}): JSX.Element {
  const { control, watch, setValue, getValues } = formMethods;

  const watchedTotalAmount = watch('rateData.customerTotalCharge.val');
  const watchedCarrierLineHaulCharge = watch(
    'rateData.carrierLineHaulCharge.val'
  );
  const watchedCustomerLineHaulCharge = watch(
    'rateData.customerLineHaulCharge.val'
  );
  const watchedCarrierCost = watch('rateData.carrierCost');
  const watchedCustomerFuelSurcharge = watch('rateData.fscFlatRate');
  const carrierFuelSurchargeFieldName =
    'rateData.carrierLineItems.0.totalChargeUSD' as const;

  const watchedFuelSurchargeBuy = watch(carrierFuelSurchargeFieldName) ?? 0;

  useEffect(() => {
    if (!showBuySellFields) {
      return;
    }

    const existingCarrierLineItems =
      getValues('rateData.carrierLineItems') ?? [];
    const existingCarrierLineHaulCharge =
      Number(getValues('rateData.carrierLineHaulCharge.val')) || 0;
    const existingCarrierCost = Number(getValues('rateData.carrierCost')) || 0;
    const [normalizedFuelLineItem] = normalizeTaiFuelCarrierLineItems(
      existingCarrierLineItems
    );

    if (
      existingCarrierLineHaulCharge === 0 &&
      normalizedFuelLineItem.totalChargeUSD === 0 &&
      existingCarrierCost > 0
    ) {
      setValue('rateData.carrierLineHaulCharge.val', existingCarrierCost);
    }

    if (
      existingCarrierLineItems.length !== 1 ||
      existingCarrierLineItems[0]?.label !== normalizedFuelLineItem.label ||
      existingCarrierLineItems[0]?.unitBasis !==
        normalizedFuelLineItem.unitBasis ||
      existingCarrierLineItems[0]?.quantity !==
        normalizedFuelLineItem.quantity ||
      existingCarrierLineItems[0]?.ratePerUnitUSD !==
        normalizedFuelLineItem.ratePerUnitUSD ||
      existingCarrierLineItems[0]?.totalChargeUSD !==
        normalizedFuelLineItem.totalChargeUSD ||
      existingCarrierLineItems[0]?.note !== normalizedFuelLineItem.note
    ) {
      setValue('rateData.carrierLineItems', [normalizedFuelLineItem]);
    }
  }, [getValues, setValue, showBuySellFields]);

  useEffect(() => {
    const lineHaul = Number(watchedCustomerLineHaulCharge) || 0;
    const fuelSurcharge = Number(watchedCustomerFuelSurcharge) || 0;
    const calculatedTotal = lineHaul + fuelSurcharge;

    if (calculatedTotal !== watchedTotalAmount) {
      setValue('rateData.customerTotalCharge.val', calculatedTotal, {
        shouldValidate: true,
      });
    }
  }, [
    watchedCustomerLineHaulCharge,
    watchedCustomerFuelSurcharge,
    watchedTotalAmount,
    setValue,
  ]);

  useEffect(() => {
    if (!showBuySellFields) {
      return;
    }

    const lineHaulBuy = Number(watchedCarrierLineHaulCharge) || 0;
    const fuelSurchargeBuy = Number(watchedFuelSurchargeBuy) || 0;
    const calculatedCarrierTotal = lineHaulBuy + fuelSurchargeBuy;

    if (calculatedCarrierTotal !== watchedCarrierCost) {
      setValue('rateData.carrierCost', calculatedCarrierTotal, {
        shouldValidate: true,
      });
    }
  }, [
    watchedCarrierLineHaulCharge,
    watchedFuelSurchargeBuy,
    watchedCarrierCost,
    setValue,
    showBuySellFields,
  ]);

  return (
    <Grid cols='2' gap='sm' className='mx-0 w-full'>
      <div className='col-span-1 text-md text-neutral-800 font-semibold mb-1'>
        Customer
      </div>

      {showBuySellFields ? (
        <>
          <Grid cols='2' gap='sm' className='mx-0 w-full col-span-2'>
            <Typography variant='body-sm' weight='semibold'>
              Buy
            </Typography>
            <Typography variant='body-sm' weight='semibold'>
              Sell
            </Typography>
          </Grid>

          <Grid cols='2' gap='sm' className='mx-0 w-full col-span-2'>
            <div className='col-span-1'>
              <LoadBuildingTextInput
                name='rateData.carrierLineHaulCharge.val'
                label='Linehaul'
                inputType='number'
                options={{ valueAsNumber: true }}
                required={false}
                aiIconOnly={true}
              />
            </div>

            <div className='col-span-1'>
              <LoadBuildingTextInput
                name='rateData.customerLineHaulCharge.val'
                label='Linehaul'
                inputType='number'
                options={{ valueAsNumber: true }}
                required={false}
                aiIconOnly={true}
              />
            </div>
          </Grid>

          <Grid cols='2' gap='sm' className='mx-0 w-full col-span-2'>
            <div className='col-span-1'>
              <LoadBuildingTextInput
                name={carrierFuelSurchargeFieldName}
                label='Fuel'
                inputType='number'
                options={{ valueAsNumber: true }}
                required={false}
                aiIconOnly={true}
              />
            </div>

            <div className='col-span-1'>
              <LoadBuildingTextInput
                name='rateData.fscFlatRate'
                label='Fuel'
                inputType='number'
                options={{ valueAsNumber: true }}
                required={false}
                aiIconOnly={true}
              />
            </div>
          </Grid>

          <Grid cols='2' gap='sm' className='mx-0 w-full col-span-2'>
            <div className='col-span-1'>
              <LoadBuildingTextInput
                name='rateData.carrierCost'
                label='Total'
                inputType='number'
                options={{ valueAsNumber: true }}
                required={false}
                readOnly
              />
            </div>
            <div className='col-span-1'>
              <LoadBuildingTextInput
                name='rateData.customerTotalCharge.val'
                label='Total'
                inputType='number'
                options={{ valueAsNumber: true }}
                required={false}
                readOnly
              />
            </div>
          </Grid>
        </>
      ) : (
        <>
          <Grid cols='2' gap='sm' className='mx-0 w-full col-span-2'>
            <div className='col-span-1'>
              <LoadBuildingTextInput
                name='rateData.customerLineHaulCharge.val'
                label='Line Haul Charge'
                inputType='number'
                options={{ valueAsNumber: true }}
                required={false}
                aiIconOnly={true}
              />
            </div>

            <div className='col-span-1'>
              <LoadBuildingTextInput
                name='rateData.fscFlatRate'
                label='Fuel Surcharge'
                inputType='number'
                options={{ valueAsNumber: true }}
                required={false}
                aiIconOnly={true}
              />
            </div>
          </Grid>

          <div className='col-span-2'>
            <LoadBuildingTextInput
              name='rateData.customerTotalCharge.val'
              label='Total Amount'
              inputType='number'
              options={{ valueAsNumber: true }}
              required={false}
              readOnly
            />
          </div>
        </>
      )}
      <Flex direction='col' className='col-span-2'>
        <Label
          name={'rateData.customerTotalCharge.unit'}
          required={
            watchedTotalAmount > 0 ||
            watchedCustomerLineHaulCharge > 0 ||
            (showBuySellFields && (watchedCarrierCost ?? 0) > 0) ||
            (watchedCustomerFuelSurcharge ?? 0) > 0
          }
        >
          Currency
        </Label>
        <Controller
          name='rateData.customerTotalCharge.unit'
          control={control}
          rules={{
            required:
              watchedTotalAmount > 0 ||
              watchedCustomerLineHaulCharge > 0 ||
              (showBuySellFields && (watchedCarrierCost ?? 0) > 0) ||
              (watchedCustomerFuelSurcharge ?? 0) > 0,
          }}
          render={({ field }) => (
            <Select
              showSearch
              className='h-8'
              placeholder={'Choose'}
              optionFilterProp='children'
              onChange={field.onChange}
              value={field.value}
              disabled={isUpdateMode}
              options={currencyList?.map((currency) => ({
                value: currency,
                label: currency,
              }))}
            />
          )}
        />
      </Flex>
    </Grid>
  );
}
